import { Directive } from '@angular/core';

import * as uuid from 'uuid';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AiQuickTipEnums } from '../../../Forms/enums/ai-quick-tip.enum';
import { AiGeneratedContentModel } from '../../../Forms/models/ai-generated-content-card.model';


@Directive()
export abstract class AbstractAiDialogComponent extends AbstractDialogComponent {

    prompt: string;
    loading: boolean = false;
    hasGenerated: boolean = true;
    doneStreaming: boolean = false;
    streamingStarted: boolean = false;
    generatedContent: string = '';
    originalTextCollapsed: boolean = true;
    agreedOriginalText: boolean = false;
    tryToSubmit: boolean = false;
    contentElements: AiGeneratedContentModel[] = [];
    aiConversationStack: string[] = [];
    abortController;
    retryCount: number = 0;

    reader: ReadableStreamDefaultReader<Uint8Array>;
    readonly endFormatJson: string[] = ['','}',']}','"]}','""]}',',""]}','[]}',':[]}','":[]}','"":[]}','"}',
        ']','}]','"}]','""}]',',""}]','{}]',':{}]','":{}]','"":{}]','"]','":""}]','"":""}]'];

    readonly retryLimit: number = 3;

    constructor(
        public coreDialogService: CoreDialogService,
        public dialogKey: string,
    ) {
        super(coreDialogService, dialogKey);
    }

    fetchData(url: string, aiType?: string, aiReferenceId?: string) {

        const uint8array = new TextDecoder();

        if (!aiReferenceId) {
            aiReferenceId = uuid.v4();
        }

        this.abortController = new AbortController();
        fetch(url, {
            'headers': {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'Accept': 'text/plain, application/json',
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'content-type': 'application/json',
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'Ai-Reference-Id': aiReferenceId
            },
            'body': JSON.stringify(this.getRequestModel(aiType)),
            'method': 'POST',
            'mode': 'cors',
            'credentials': 'include',
            signal: this.abortController.signal
        }).then(
            (response) => {
                if (response.status !== 200 && response.status !== 204) {
                    // should give us some context as to why the call fails (at least for now)
                    console.log('response', response);
                    throw new Error;
                }
                this.reader = response.body.getReader();
                this.loading = false;
                const pump = (controller) => {
                    return this.reader.read().then(({ done, value }) => {
                        let nextValue = uint8array.decode(value);
                        let multipleElements = false;
                        if (aiType && this.contentElements?.length) {
                            multipleElements = true;
                        }

                        if (this.generatedContent === '') {
                            if (nextValue.trim() === '.') {
                                // Handle the case of some amount of whitespace around a .
                                nextValue = '';
                            } else {
                                // handle the case of starting white space but otherwise good content
                                nextValue = nextValue.trimStart();

                                // handle OpenAI bug that returns undesired fence code block wrapper
                                if (nextValue.startsWith('```json')) {
                                    nextValue = nextValue.replace('```json', '');
                                }

                                if (nextValue.startsWith('json')) {
                                    nextValue = nextValue.replace('json', '');
                                }

                                if (nextValue.startsWith('```html')) {
                                    nextValue = nextValue.replace('```html', '');
                                }

                                if (nextValue.startsWith('html')) {
                                    nextValue = nextValue.replace('html', '');
                                }
                            }
                        }

                        if (nextValue.endsWith('```')) {
                            nextValue = nextValue.replace('```', '');
                        }

                        this.generatedContent += nextValue;
                        this.extractContent(aiType);

                        if (multipleElements) {
                            const element = this.contentElements.find(x => x.id === aiType);
                            const regexStr = `{[\\r\\n]*[ ]*"${aiType}": "|}"|"[ ]*[\\r\\n]*[ ]*}`;
                            // openAi returns both subheadline and subHeadline, so keep here case insensitive
                            const regex = new RegExp(regexStr, 'ig');
                            // Dynamically extract JSON value like:  {"subjectLine": "Nike Unveils New Product Line"}
                            if (regex.test(this.generatedContent)) {
                                let jsonValue = this.generatedContent;
                                jsonValue = jsonValue.replace(regex, '');
                                element.value = jsonValue.replace(/\\n/g, '<br>').replace(/\\"/g, '"').trimStart();
                            }
                        }

                        // When no more data needs to be consumed, close the stream
                        if (done) {
                            controller.close();
                            this.reader?.cancel();

                            if (!this.generatedContent) {
                                if (this.retryCount < this.retryLimit) {
                                    this.fetchData(url, aiType, aiReferenceId);
                                    this.retryCount++;
                                    return;
                                } else {
                                    this.retryCount = 0;
                                    this.handleError(aiType, response.status);
                                }
                            }

                            if (!this.aiConversationStack.length) {
                                this.doneStreaming = true;
                                this.hasGenerated = true;
                            }
                            this.onComplete(aiType);
                            return;
                        }
                        // Enqueue the next data chunk into our target stream
                        controller.enqueue(value);
                        return pump(controller);
                    });
                };
                const startFunction = (controller) => {
                    return pump(controller);
                };
                return new ReadableStream({
                    start: startFunction
                });
            }
        ).catch(
            error => {
                if (error?.message && error.message.includes('signal is aborted without reason')) {
                    return;
                }
                this.handleError(aiType);
            }
        );

        /*
        There's some problem with this approach, the streaming takes a while to connect and comes in a big chunk in the beginning,
        switching to fetch instead, for now
        this.aiApi.postCompletions(this.getPrompt()).subscribe({
            next: (res: any) => {
                console.log(res);
                if (res?.type === 3) {
                    this.loading = false;
                    if (res.partialText) {
                        this.generatedContent = res.partialText;
                        if (this.generatedContent[0] === '.') {
                            this.generatedContent = this.generatedContent.slice(1);
                        }
                        this.generatedContent = this.generatedContent.trim();
                    }
                    this.generatedContent = res.partialText;
                // type 4 means streaming is complete
                } else if (res?.type === 4) {
                    this.doneStreaming = true;
                }
                this.hasGenerated = true;
            },
            error: (error) => {
                console.log('error with streaming', error);
                this.loading = false;
            }
        });
        */
    }

    setInsightJson(response: string): any {
        let insightJson: any;
        for (const endFormat of this.endFormatJson) {
            if (!insightJson) {
                try {
                    insightJson = JSON.parse(response + endFormat);
                } catch (e) {
                    // do nothing, format still doesn't make sense from the streaming
                }
            }
        }
        return insightJson;
    }

    abortFetching(): void {
        this.abortController?.abort();
    }

    abstract handleError(aiType?: string, httpStatusCode?: number);
    abstract getRequestModel(aiType?: string);
    showQuickTip(type: AiQuickTipEnums){}
    onComplete(aiType?: string){}
    extractContent(aiType?: string) {}
}
